import { useListBookmarkedVouchers } from '@/api/bookmarks/bookmarks';
import type { VoucherCodeResponse } from '@/api/model/voucherCodeResponse';
import type { VoucherResponse } from '@/api/model/voucherResponse';
import { useListMyVoucherCodes } from '@/api/voucher-codes/voucher-codes';
import { Badge } from '@/components/ui/badge';
import { VoucherCard } from '@/components/vouchers/VoucherCard';
import { VoucherGridBase } from '@/components/vouchers/VoucherGridBase';
import type { VoucherSortOrder } from '@/components/vouchers/VoucherFilterControls';
import type { VoucherStatusFilter } from '@/pages/MyVoucherFilterBar';
import React, { useMemo, useState } from 'react';

type MyVoucherGridContainerProps = {
  selectedCategory: string;
  searchQuery: string;
  voucherType: 'activated' | 'bookmarked';
  selectedStatus: VoucherStatusFilter;
  sortOrder: VoucherSortOrder;
  onVoucherClick: (code: VoucherCodeResponse) => void;
  onBookmarkedVoucherClick?: (voucher: VoucherResponse) => void;
};

export const MyVoucherGridContainer: React.FC<MyVoucherGridContainerProps> = ({
  selectedCategory,
  searchQuery,
  voucherType,
  selectedStatus,
  sortOrder,
  onVoucherClick,
  onBookmarkedVoucherClick,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Build user voucher query parameters
  const voucherParams = useMemo(() => ({ page: 1, per_page: 100 }), []);

  const {
    data: myCodesData,
    isLoading: isLoadingCodes,
    isError: isErrorCodes,
    error: errorCodes,
  } = useListMyVoucherCodes(voucherParams);

  // Fetch bookmarked vouchers
  const {
    data: bookmarkedVouchersData,
    isLoading: isLoadingBookmarks,
    isError: isErrorBookmarks,
    error: errorBookmarks,
  } = useListBookmarkedVouchers(voucherType === 'bookmarked' ? { page: 1, per_page: 100 } : undefined, {
    query: {
      enabled: voucherType === 'bookmarked',
    },
  });

  // Determine which data source to use
  const isLoading = voucherType === 'bookmarked' ? isLoadingBookmarks : isLoadingCodes;
  const isError = voucherType === 'bookmarked' ? isErrorBookmarks : isErrorCodes;
  const error = voucherType === 'bookmarked' ? errorBookmarks : errorCodes;

  // Filter vouchers by category, search, and status client-side
  const filteredItems = useMemo(() => {
    if (voucherType === 'bookmarked') {
      // For bookmarked vouchers, use the bookmarked vouchers data directly
      let vouchers: VoucherResponse[] = bookmarkedVouchersData?.items || [];

      // Filter by category
      if (selectedCategory !== 'alle') {
        vouchers = vouchers.filter(v => v.provider.category === selectedCategory);
      }

      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        vouchers = vouchers.filter(
          v => v.title.toLowerCase().includes(query) || v.provider.name.toLowerCase().includes(query)
        );
      }

      return vouchers;
    }

    // For activated vouchers, use the existing logic
    let codes = myCodesData?.items || [];

    // Filter by status
    if (selectedStatus !== 'all') {
      if (selectedStatus === 'REDEEMED') {
        codes = codes.filter(c => Boolean(c.redeemed_at));
      } else if (selectedStatus === 'ACTIVE') {
        codes = codes.filter(c => !c.redeemed_at && Boolean(c.activated_at));
      }
    }

    // Filter by category
    if (selectedCategory !== 'alle') {
      codes = codes.filter(c => c.voucher && c.voucher.provider.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      codes = codes.filter(
        c =>
          c.voucher &&
          (c.voucher.title.toLowerCase().includes(query) || c.voucher.provider.name.toLowerCase().includes(query))
      );
    }

    return codes;
  }, [myCodesData?.items, bookmarkedVouchersData?.items, selectedCategory, searchQuery, voucherType, selectedStatus]);

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      // Handle both VoucherResponse and VoucherCodeResponse
      const aVoucher = 'voucher' in a ? a.voucher : a;
      const bVoucher = 'voucher' in b ? b.voucher : b;
      const aTime = Date.parse(('created_at' in a ? a.created_at : aVoucher?.created_at) ?? '');
      const bTime = Date.parse(('created_at' in b ? b.created_at : bVoucher?.created_at) ?? '');

      if (Number.isNaN(aTime) && Number.isNaN(bTime)) {
        return 0;
      }

      if (Number.isNaN(aTime)) {
        return sortOrder === 'newest' ? 1 : -1;
      }

      if (Number.isNaN(bTime)) {
        return sortOrder === 'newest' ? -1 : 1;
      }

      return sortOrder === 'newest' ? bTime - aTime : aTime - bTime;
    });
  }, [filteredItems, sortOrder]);

  const totalVouchers = sortedItems.length;
  // Simple local pagination UI for filtered results
  const pageSize = 6;
  const totalPages = Math.max(1, Math.ceil(totalVouchers / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedItems = sortedItems.slice(startIndex, startIndex + pageSize);

  const goToPage = (page: number) => setCurrentPage(Math.min(Math.max(1, page), totalPages));

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery, voucherType, selectedStatus, sortOrder]);

  const getEmptyMessage = () => {
    if (voucherType === 'bookmarked') {
      return 'Sie haben noch keine Gutscheine vorgemerkt. Entdecken Sie interessante Angebote und merken Sie sie vor!';
    }

    if (selectedCategory !== 'alle' || searchQuery) {
      return 'Für diese Filter wurden keine Gutscheine gefunden. Versuchen Sie andere Suchkriterien.';
    }

    return 'Sie haben noch keine Gutscheine gekauft. Entdecken Sie unsere aktuellen Angebote!';
  };

  return (
    <VoucherGridBase
      items={paginatedItems}
      isLoading={isLoading}
      isError={isError}
      error={error}
      emptyMessage={getEmptyMessage()}
      currentPage={currentPage}
      totalPages={totalPages}
      totalVouchers={totalVouchers}
      onPageChange={goToPage}
      renderItem={item => {
        // Handle both VoucherCodeResponse (activated) and VoucherResponse (bookmarked)
        const isCode = 'voucher' in item;

        // For bookmarked vouchers, render directly like in VoucherGridContainer
        if (!isCode) {
          const voucher = item as VoucherResponse;
          return (
            <VoucherCard
              key={voucher.id}
              voucher={voucher}
              onClick={() => {
                // If a specific handler for bookmarked vouchers is provided, use it
                // This allows bookmarked vouchers to behave like vouchers in the overview
                if (onBookmarkedVoucherClick) {
                  onBookmarkedVoucherClick(voucher);
                } else {
                  // Fallback: Create a mock code structure for backward compatibility
                  const voucherCopy: VoucherResponse = {
                    ...voucher,
                    provider: {
                      ...voucher.provider,
                    },
                    images: voucher.images ? [...voucher.images] : voucher.images,
                    description: voucher.description,
                    conditions: voucher.conditions,
                    max_redemptions: voucher.max_redemptions,
                    redemption_type: voucher.redemption_type,
                  };

                  const mockCode: VoucherCodeResponse = {
                    id: voucher.id,
                    voucher_id: voucher.id,
                    code: '',
                    user_id: null,
                    activated_at: null,
                    redeemed_at: null,
                    batch_id: voucher.id,
                    created_at: voucher.created_at,
                    voucher: voucherCopy,
                    pdfUrl: undefined,
                  } as VoucherCodeResponse;
                  onVoucherClick(mockCode);
                }
              }}
            />
          );
        }

        // For activated vouchers, show badges and PDF button
        const code = item as VoucherCodeResponse;
        const voucher = code.voucher;

        if (!voucher) {
          return null;
        }

        return (
          <div key={code.id} className='relative'>
            {code.redeemed_at && (
              <Badge
                variant='secondary'
                className='absolute left-3 top-3 z-10 border-0 bg-swd-gruen-light text-swd-tuerkis-darker text-xs font-semibold leading-4 px-3 py-1 rounded-lg shadow-sm'
              >
                Eingelöst
              </Badge>
            )}
            <VoucherCard
              voucher={voucher}
              onClick={() => onVoucherClick(code)}
              hideBookmark={true}
              pdfUrl={code.pdfUrl}
            />
          </div>
        );
      }}
    />
  );
};
